import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoginForm } from './login-form';
import { SignupForm } from './signup-form';
import { Sparkles, Users, Shield, Heart } from 'lucide-react';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthDialog({ isOpen, onClose }: AuthDialogProps) {
  const [activeTab, setActiveTab] = useState('login');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-full mx-4 p-0 bg-white dark:bg-slate-900 border-0 shadow-2xl rounded-3xl overflow-hidden">
        <div className="flex min-h-[600px]">
          {/* Left Side - Branding */}
          <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full mix-blend-overlay filter blur-xl opacity-70 animate-blob"></div>
              <div className="absolute top-0 right-4 w-40 h-40 bg-white rounded-full mix-blend-overlay filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-8 left-20 w-40 h-40 bg-white rounded-full mix-blend-overlay filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
            </div>
            
            <div className="relative z-10 flex flex-col justify-center items-center text-white p-12 text-center">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
                  <Sparkles className="w-10 h-10" />
                </div>
                <h1 className="text-4xl font-bold mb-4">Welcome to InTouch</h1>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Connect with people, share your thoughts, and discover amazing content in our vibrant community
                </p>
              </div>
              
              <div className="space-y-6 max-w-sm">
                <div className="flex items-center gap-4 text-white/80">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">Connect & Share</h3>
                    <p className="text-sm text-white/70">Build meaningful relationships</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-white/80">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">Safe & Secure</h3>
                    <p className="text-sm text-white/70">Your privacy is our priority</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-white/80">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Heart className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">Community First</h3>
                    <p className="text-sm text-white/70">Join millions of active users</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Auth Forms */}
          <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col">
            <div className="flex-1 flex flex-col justify-center">
              <div className="w-full max-w-md mx-auto">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-gray-100 dark:bg-slate-800 rounded-xl p-1 mb-8 h-12">
                    <TabsTrigger 
                      value="login" 
                      className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-sm transition-all duration-200 font-medium"
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger 
                      value="signup" 
                      className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:shadow-sm transition-all duration-200 font-medium"
                    >
                      Create Account
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="login" className="mt-0">
                    <LoginForm onSuccess={onClose} onSwitchToSignup={() => setActiveTab('signup')} />
                  </TabsContent>
                  
                  <TabsContent value="signup" className="mt-0">
                    <SignupForm onSuccess={onClose} onSwitchToLogin={() => setActiveTab('login')} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}